import { Component, OnDestroy, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, interval, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Apidata } from '../../../services/apidata';
import { Translatable } from '../../../models/translatable.model';
import { Slider } from '../../../models/slider.model';
import { Post } from '../../../models/post.model';
import { Herovisual } from '../herovisual/herovisual';
import { LoaderService } from '../../../services/loader.service';

@Component({
  selector: 'app-hero',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule, Herovisual],
  templateUrl: './hero.html',
  styleUrl: './hero.css',
})
export class Hero implements OnInit, OnDestroy {
  private apidata   = inject(Apidata);
  private translate = inject(TranslateService);
  private loader    = inject(LoaderService);

  // ── État ───────────────────────────────────────────────────────────────
  heros: Slider[] = [];
  heroBox: Post[] = [];

  /** Index du slide actif (animation contrôlée par signal) */
  activeIndex = signal(0);

  /** Slide courant calculé */
  current = computed<Slider | null>(() => this.heros[this.activeIndex()] ?? null);

  /** Vitesse de rotation (ms) — pause si pas plus d'un slide */
  private readonly INTERVAL_MS = 7000;
  private autoSub?: Subscription;
  private subs: Subscription[] = [];

  // ── Cycle ──────────────────────────────────────────────────────────────
  ngOnInit(): void {
    // ── Filet de sécurité : si les API tardent, on cache le loader après 2.5s
    //     pour laisser l'utilisateur voir le hero (fallback statique) ────
    const safetyTimer = setTimeout(() => this.loader.forceHide(), 2500);

    // ── Chargement en parallèle des 2 endpoints critiques du hero ─────
    this.subs.push(
      forkJoin({
        slides: this.apidata.loadSlides(),
        posts:  this.apidata.loadLastestPosts(),
      })
      .pipe(
        finalize(() => {
          // Hero prêt → on libère le loader global, les autres sections
          // (différées via @defer) chargeront en arrière-plan.
          clearTimeout(safetyTimer);
          this.loader.forceHide();
        })
      )
      .subscribe({
        next: ({ slides, posts }) => {
          this.heros = slides.data ?? [];
          this.heroBox = posts.data ?? [];
          this.activeIndex.set(0);
          this.startAutoplay();
        },
        error: err => {
          console.error('Hero data error', err);
          clearTimeout(safetyTimer);
          this.loader.forceHide();
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.autoSub?.unsubscribe();
    this.subs.forEach(s => s.unsubscribe());
  }

  // ── Autoplay ───────────────────────────────────────────────────────────

  private startAutoplay(): void {
    this.autoSub?.unsubscribe();
    if (this.heros.length <= 1) return;
    this.autoSub = interval(this.INTERVAL_MS).subscribe(() => this.next());
  }

  next(): void {
    if (!this.heros.length) return;
    this.activeIndex.set((this.activeIndex() + 1) % this.heros.length);
  }

  prev(): void {
    if (!this.heros.length) return;
    const i = this.activeIndex();
    this.activeIndex.set(i === 0 ? this.heros.length - 1 : i - 1);
  }

  goTo(i: number): void {
    this.activeIndex.set(i);
    // Redémarre le timer pour éviter une transition immédiate
    this.startAutoplay();
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  currentLang(): string {
    return this.translate.currentLang || 'fr';
  }

  translateField(field: Translatable | null | undefined): string {
    if (!field) return '';
    const lang = this.translate.currentLang as 'fr' | 'en' | 'ar';
    return field[lang] ?? field.fr ?? '';
  }

  /** Liste tronquée des 3 derniers articles pour la card côté droit */
  get latestArticles(): Post[] {
    return (this.heroBox ?? []).slice(0, 3);
  }

  trackByID(_: number, slide: Slider): number { return slide.id; }
}
